import {
  Button,
  Card,
  ErrorBox,
  Field,
  LineChart,
  Link,
  Note,
  Page,
  Select,
  Stat,
  Table,
  Td,
} from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fmtDay, fmtLei, fmtMonth, fmtNum, plural } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, must, rpc } from '../lib/supabase.ts';
import type { InactiveClient, InactivityAlert, SalesStats } from '../lib/types.ts';
import { historyLabel } from './common.tsx';
import { BusyButton, Refreshing, SkeletonCards, SkeletonChart, SkeletonTable } from './loading.tsx';

// Vanzarile firmei (docs/25): achizitiile directe in care firma e furnizor in SEAP, prin RLS
// (istoricul planului, codurile din abonament, judetele agentului). Permisiunea sales.view.

/** Pragurile clientilor inactivi (aceleasi ca in SQL: app.inactive_clients). */
export const INACTIVITY = [
  { months: 1, label: 'o lună' },
  { months: 2, label: '2 luni' },
  { months: 3, label: '3 luni' },
  { months: 6, label: '6 luni' },
  { months: 12, label: 'un an' },
] as const;

export const PERIODS: { months: number | null; label: string }[] = [
  { months: null, label: 'Tot istoricul' },
  { months: 12, label: 'Ultimele 12 luni' },
  { months: 6, label: 'Ultimele 6 luni' },
  { months: 3, label: 'Ultimele 3 luni' },
  { months: 1, label: 'Luna aceasta' },
];

const monthsLabel = (m: number) => INACTIVITY.find((x) => x.months === m)?.label ?? `${m} luni`;

/** Zilele dintre doua date 'YYYY-MM-DD', aproximate in luni. */
const monthsBetween = (from: string, to: Date) =>
  (to.getTime() - Date.parse(`${from}T00:00:00Z`)) / (30.44 * 86_400_000);

export function NotLinked() {
  const { can } = useOrg();
  return (
    <Note>
      Firma nu e legată încă de fișa ei de furnizor din SEAP, deci nu știm ce a vândut.{' '}
      {can('settings.manage') ? (
        <>
          O legi din{' '}
          <Link to="/app/setari" className="font-medium underline">
            Setări firmă → Firma în SEAP
          </Link>
          .
        </>
      ) : (
        'Proprietarul o poate lega din Setări firmă → Firma în SEAP.'
      )}
    </Note>
  );
}

/** Lunile dintre prima si ultima vanzare, cu 0 unde n-a fost nimic (altfel graficul ar uni
 * direct lunile cu vanzari, peste cele goale). */
function fillMonths(months: NonNullable<SalesStats['by_month']>) {
  const first = months[0];
  const last = months.at(-1);
  if (!first || !last) return [];
  const by = new Map(months.map((m) => [m.month.slice(0, 7), m]));
  const out: NonNullable<SalesStats['by_month']> = [];
  const d = new Date(`${first.month.slice(0, 7)}-01T00:00:00Z`);
  const end = Date.parse(`${last.month.slice(0, 7)}-01T00:00:00Z`);
  while (d.getTime() <= end) {
    const key = d.toISOString().slice(0, 7);
    out.push(by.get(key) ?? { month: `${key}-01`, acquisitions: 0, value_ron: 0 });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return out;
}

/** O bara orizontala, cat din maxim (pentru judete). */
export function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 && value > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mt-1 h-1.5 w-full bg-gri-200" aria-hidden="true">
      <div className="h-1.5 bg-cerneala" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Vanzarile (cifre, pe luni, coduri CPV, judete, clasament). `compact`: fara cifrele de sus si
 * fara judete (Zona mea le are pe ale ei, cu piata). */
export function Overview({ s, compact = false }: { s: SalesStats; compact?: boolean }) {
  const [all, setAll] = useState(false);
  const t = s.totals;
  if (!t?.acquisitions) {
    return (
      <p className="text-sm text-gri-800">
        Nicio achiziție câștigată în perioada aleasă, pe codurile din abonament.
      </p>
    );
  }
  const months = fillMonths(s.by_month ?? []);
  const counties = s.by_county ?? [];
  const maxCounty = Math.max(0, ...counties.map((c) => Number(c.value_ron)));
  const authorities = s.top_authorities ?? [];
  const shown = all ? authorities : authorities.slice(0, 15);
  return (
    <>
      {!compact ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Total vândut"
            value={fmtLei(t.value_ron)}
            sub={plural(t.acquisitions, 'achiziție câștigată', 'achiziții câștigate')}
            accent
          />
          <Stat label="Instituții client" value={fmtNum(t.authorities)} />
          <Stat label="Valoare medie" value={fmtLei(Number(t.value_ron) / t.acquisitions)} />
          <Stat
            label="Ultima vânzare"
            value={fmtDay(t.last_day)}
            sub={`prima: ${fmtDay(t.first_day)}`}
          />
        </div>
      ) : null}

      <Card title="Vânzările pe luni (lei)">
        {months.length > 1 ? (
          <LineChart
            series={[
              {
                name: 'valoare',
                color: '#444141',
                points: months.map(
                  (m) => [Date.parse(m.month) / 1000, Number(m.value_ron)] as [number, number],
                ),
              },
            ]}
            unit=" lei"
            formatX={(x) => fmtMonth(new Date(x * 1000).toISOString())}
          />
        ) : null}
        <Table head={['Luna', 'Achiziții', 'Valoare']}>
          {[...months].reverse().map((m) => (
            <tr key={m.month}>
              <Td className="whitespace-nowrap">{fmtMonth(m.month)}</Td>
              <Td className="tabular-nums">{fmtNum(m.acquisitions)}</Td>
              <Td className="text-right tabular-nums">{fmtLei(m.value_ron)}</Td>
            </tr>
          ))}
        </Table>
      </Card>

      <div className={compact ? '' : 'grid gap-3 lg:grid-cols-2'}>
        <Card title="Codurile CPV cele mai vândute">
          <Table head={['CPV', 'Achiziții', 'Valoare produse']} empty={!s.top_cpv?.length}>
            {s.top_cpv?.map((c) => (
              <tr key={c.cpv_code}>
                <Td>
                  <Link to={`/app/preturi/${c.cpv_code}`} className="font-medium hover:underline">
                    {c.cpv_code}
                  </Link>
                  <div className="text-xs text-gri-700">{c.cpv_label}</div>
                </Td>
                <Td className="tabular-nums">{fmtNum(c.acquisitions)}</Td>
                <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(c.value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
        {!compact ? (
          <Card title="Pe județe">
            <Table head={['Județul', 'Clienți', 'Valoare']} empty={!counties.length}>
              {counties.map((c) => (
                <tr key={c.county}>
                  <Td className="w-1/2">
                    {c.county}
                    <Bar value={Number(c.value_ron)} max={maxCounty} />
                  </Td>
                  <Td className="tabular-nums">{fmtNum(c.authorities)}</Td>
                  <Td className="whitespace-nowrap text-right tabular-nums">
                    {fmtLei(c.value_ron)}
                    <div className="text-xs text-gri-700">
                      {plural(c.acquisitions, 'achiziție', 'achiziții')}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </Card>
        ) : null}
      </div>

      <Card
        title="Clasamentul instituțiilor"
        actions={
          authorities.length > 15 ? (
            <Button tone="ghost" onClick={() => setAll(!all)}>
              {all ? 'Primele 15' : `Toate (${authorities.length})`}
            </Button>
          ) : null
        }
      >
        <Table head={['#', 'Instituția', 'Achiziții', 'Valoare', 'Ultima achiziție']}>
          {shown.map((a, i) => (
            <tr key={a.id ?? `t${i}`}>
              <Td className="tabular-nums text-gri-700">{i + 1}</Td>
              <Td className="max-w-md">
                {a.id ? (
                  <Link to={`/app/autoritati/${a.id}`} className="font-medium hover:underline">
                    {a.name ?? `Instituția ${a.id}`}
                  </Link>
                ) : (
                  (a.name ?? '—')
                )}
                <div className="text-xs text-gri-700">{a.county}</div>
              </Td>
              <Td className="tabular-nums">{fmtNum(a.acquisitions)}</Td>
              <Td className="whitespace-nowrap text-right tabular-nums">{fmtLei(a.value_ron)}</Td>
              <Td className="whitespace-nowrap tabular-nums">{fmtDay(a.last_day)}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

/** Pragul de notificare al utilizatorului curent. */
function InactivityAlertCard({ months, onMonths }: { months: number; onMonths(m: number): void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['inactivity_alert', org.id],
    queryFn: () =>
      must(
        app().from('inactivity_alerts').select('*').eq('organization_id', org.id).maybeSingle(),
      ) as Promise<InactivityAlert | null>,
  });
  const [email, setEmail] = useState<boolean | null>(null);
  const save = useMutation({
    mutationFn: (v: { months: number; email: boolean; enabled: boolean }) =>
      rpc<InactivityAlert>('set_inactivity_alert', {
        p_org: org.id,
        p_months: v.months,
        p_notify_email: v.email,
        p_enabled: v.enabled,
      }),
    onSuccess: () => {
      setEmail(null);
      void qc.invalidateQueries({ queryKey: ['inactivity_alert', org.id] });
    },
  });
  const a = q.data;
  const notify = email ?? a?.notify_email ?? true;
  const active = Boolean(a?.enabled);
  const same = active && a?.months === months && a?.notify_email === notify;
  return (
    <div className="space-y-2 border-2 border-gri-300 p-3 text-sm">
      <div className="font-medium">Notificări</div>
      {active && a ? (
        <p>
          Activă: te anunțăm când un client trece de <b>{monthsLabel(a.months)}</b> fără să mai
          cumpere de la voi{a.notify_email ? ', și pe email' : ''}.
        </p>
      ) : (
        <p className="text-gri-800">
          Primești o notificare (și, dacă vrei, un email) în ziua în care un client trece de pragul
          ales fără să mai cumpere de la voi.
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Pragul">
          <Select
            value={months}
            onChange={(e) => onMonths(Number(e.target.value))}
            aria-label="Pragul notificării"
          >
            {INACTIVITY.map((x) => (
              <option key={x.months} value={x.months}>
                {x.label}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-1.5 pb-2">
          <input type="checkbox" checked={notify} onChange={(e) => setEmail(e.target.checked)} />
          și pe email
        </label>
        <BusyButton
          tone="primary"
          busy={save.isPending}
          disabled={same}
          onClick={() => save.mutate({ months, email: notify, enabled: true })}
        >
          {active ? 'Salvează pragul' : 'Pornește notificările'}
        </BusyButton>
        {active && a ? (
          <Button
            tone="ghost"
            onClick={() => save.mutate({ months: a.months, email: a.notify_email, enabled: false })}
          >
            Oprește
          </Button>
        ) : null}
      </div>
      <ErrorBox error={q.error ?? save.error} />
    </div>
  );
}

export function InactiveClients({ firstDay }: { firstDay: string | null | undefined }) {
  const { org } = useOrg();
  const alert = useQuery({
    queryKey: ['inactivity_alert', org.id],
    queryFn: () =>
      must(
        app().from('inactivity_alerts').select('*').eq('organization_id', org.id).maybeSingle(),
      ) as Promise<InactivityAlert | null>,
  });
  const [picked, setPicked] = useState<number | null>(null);
  const months = picked ?? alert.data?.months ?? 3;
  const q = useQuery({
    queryKey: ['inactive_clients', org.id, months],
    queryFn: () => rpc<InactiveClient[]>('inactive_clients', { p_org: org.id, p_months: months }),
  });
  const rows = q.data ?? [];
  const tooShort = firstDay ? monthsBetween(firstDay, new Date()) < months : false;
  return (
    <Card title="Clienți inactivi">
      <div className="space-y-3">
        <p className="text-sm text-gri-800">
          Instituțiile care au cumpărat de la voi, dar nu au mai cumpărat nimic de la voi de cel
          puțin <b>{monthsLabel(months)}</b>. „Între timp” arată ce au cumpărat de atunci de la alți
          furnizori, pe codurile din abonament.
        </p>
        <InactivityAlertCard months={months} onMonths={setPicked} />
        {tooShort ? (
          <Note>
            Prima vânzare vizibilă e din {fmtDay(firstDay)}: pragul de {monthsLabel(months)} se
            umple pe măsură ce se adună istoric.
          </Note>
        ) : null}
        <ErrorBox error={q.error} />
        <Refreshing active={q.isFetching && !q.isLoading}>
          {q.isLoading ? (
            <SkeletonTable rows={4} cols={5} />
          ) : (
            <Table
              head={['Instituția', 'Ultima achiziție de la voi', 'Cu voi', 'Între timp']}
              empty={!rows.length}
            >
              {rows.map((c) => (
                <tr key={c.authority_id}>
                  <Td className="max-w-xs">
                    <Link
                      to={`/app/autoritati/${c.authority_id}`}
                      className="font-medium hover:underline"
                    >
                      {c.name ?? `Instituția ${c.authority_id}`}
                    </Link>
                    <div className="text-xs text-gri-700">{c.county}</div>
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {fmtDay(c.last_day)}
                    <div className="text-xs text-gri-700">
                      acum {plural(c.days_since, 'zi', 'zile')}
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {fmtLei(c.value_ron)}
                    <div className="text-xs text-gri-700">
                      {plural(c.acquisitions, 'achiziție', 'achiziții')}
                    </div>
                  </Td>
                  <Td className="max-w-xs">
                    {c.others_after ? (
                      <>
                        {plural(c.others_after, 'achiziție', 'achiziții')} de la alții, ultima{' '}
                        {fmtDay(c.others_last_day)}
                        {c.others_top_supplier ? (
                          <div className="text-xs text-gri-700">
                            cel mai des: {c.others_top_supplier}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-gri-700">nimic pe codurile voastre</span>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Refreshing>
      </div>
    </Card>
  );
}

export function SalesPage() {
  const { org, entitlements } = useOrg();
  const [period, setPeriod] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['sales_stats', org.id, period],
    queryFn: () => rpc<SalesStats>('sales_stats', { p_org: org.id, p_months: period }),
  });
  const s = q.data;
  return (
    <Page
      title="Vânzări"
      actions={
        <Select
          aria-label="Perioada"
          value={period ?? ''}
          onChange={(e) => setPeriod(e.target.value ? Number(e.target.value) : null)}
          className="w-auto"
        >
          {PERIODS.map((p) => (
            <option key={p.label} value={p.months ?? ''}>
              {p.label}
            </option>
          ))}
        </Select>
      }
    >
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <>
          <SkeletonCards count={4} />
          <Card title="Vânzările pe luni (lei)">
            <SkeletonChart />
          </Card>
          <SkeletonTable rows={6} cols={5} />
        </>
      ) : null}
      {s && !s.supplier ? <NotLinked /> : null}
      {s?.supplier ? (
        <Refreshing active={q.isFetching && !q.isLoading}>
          <div className="space-y-4">
            <p className="-mt-2 text-xs text-gri-700">
              Achizițiile directe câștigate de <b>{s.supplier.name}</b>
              {s.supplier.cui ? ` (CUI ${s.supplier.cui})` : ''}, pe codurile CPV din abonament, în
              istoricul planului ({historyLabel(entitlements?.features.history_days)}).
            </p>
            <Overview s={s} />
            <InactiveClients firstDay={s.totals?.first_day} />
          </div>
        </Refreshing>
      ) : null}
    </Page>
  );
}
